import { useState } from "react";
import { useSearch, useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import {
  CheckCircle2, AlertCircle, Loader2, Shield, User, ChevronDown, ChevronUp, FileText, IdCard, Copy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import AgreementSignature, { canSubmitSignature, isValidPrintedName, isValidSignatureDataUrl } from "@/components/AgreementSignature";
import AgreementViewer from "@/components/AgreementViewer";
import AgreementRevisionNotice from "@/components/AgreementRevisionNotice";
import type { SignatureSource } from "@shared/agreement";
import { DocusealForm } from "@docuseal/react";
import { isCreditableReferrer } from "@/lib/referralCookie";

const VITE_APP_LOGO = import.meta.env.VITE_APP_LOGO;

export default function JoinAgent() {
  const search = useSearch();
  const [, navigate] = useLocation();
  const params = new URLSearchParams(search);
  const referredBy = params.get("ref") ?? "";
  // All agents invite as Associate only. Senior Associate is earned via admin-approved upgrade request.
  const maxAllowedLevel = "Associate";

  // "id" (4th required step — legal name/address + government ID upload) and
  // "welcome" (immediate dashboard-access credentials, ID pending admin
  // review) replace the old flow's dead-end "submitted" step, which never
  // collected ID and never actually granted access — see
  // agentSelfReg.completeSelfRegistration in server/routers.ts.
  const [step, setStep] = useState<"info" | "agreement" | "w9" | "id" | "welcome">("info");
  // The "I Agree" checkbox was removed at the client's request on 2026-09-15.
  // Drawing a signature replaces it as the act of assent, which is both a more
  // deliberate act and what the approved agreement's own acknowledgment block
  // asks for.
  //
  // The signature. Captured at the agreement step but persisted much later, in
  // agentSelfReg.submit: the rep row does not exist yet here, and there is no
  // session to authorize agreement.accept, so it has to travel with the submit.
  const [agreementPrintedName, setAgreementPrintedName] = useState("");
  const [agreementNameError, setAgreementNameError] = useState<string | null>(null);
  const [agreementSignature, setAgreementSignature] = useState<string | null>(null);
  const [agreementSignatureSource, setAgreementSignatureSource] = useState<SignatureSource>("drawn");
  const [agreementSignatureError, setAgreementSignatureError] = useState<string | null>(null);
  const [w9EmbedSrc, setW9EmbedSrc] = useState<string | null>(null);
  const [w9SubmissionId, setW9SubmissionId] = useState<number | null>(null);
  const [w9Signed, setW9Signed] = useState(false);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  // Set once agentSelfReg.submit succeeds — repCode/verifyToken are what
  // authorize the ID-upload step (identity.completeSelfRegistration) without
  // a login session, since credentials don't exist yet at this point.
  const [registeredRepCode, setRegisteredRepCode] = useState<string | null>(null);
  const [verifyToken, setVerifyToken] = useState<string | null>(null);
  const [idForm, setIdForm] = useState({ legalFullName: "", residentialAddress: "" });
  const [idFile, setIdFile] = useState<File | null>(null);
  const [idUploading, setIdUploading] = useState(false);
  const [credentials, setCredentials] = useState<{ username: string; tempPassword: string | null; dashboardUrl: string } | null>(null);

  // Fetch inviting rep details for a personalised welcome
  const inviterQuery = trpc.rep.getByCode.useQuery(
    { repCode: referredBy },
    { enabled: !!referredBy, retry: false }
  );
  const inviterName = inviterQuery.data
    ? `${inviterQuery.data.firstName} ${inviterQuery.data.lastName}`
    : referredBy;

  // Client decision (2026-08-13, BC-607): a working recruiter invite link is
  // now REQUIRED to register as an associate - self-registration must always
  // be traceable to a real upline/agency. A missing ref, or a ref that
  // doesn't resolve to a real/active rep, blocks the whole flow up front
  // instead of silently completing with blank upline/agency data. The
  // server (agentSelfReg.submit) enforces this independently - this is a
  // UX gate, not the security boundary.
  const hasRef = !!referredBy.trim();
  const referrerLoading = hasRef && inviterQuery.isLoading;
  const referrerValid = hasRef && isCreditableReferrer(inviterQuery.data as { status?: string } | null | undefined);
  const referrerInvalid = !referrerLoading && !referrerValid;

  const submitMutation = trpc.agentSelfReg.submit.useMutation({
    onSuccess: (data) => {
      setRegisteredRepCode(data.repCode);
      setVerifyToken(data.verifyToken ?? null);
      setStep("id");
    },
    onError: (e: any) => toast.error(e.message || "Submission failed. Please try again."),
  });
  const createW9SessionMutation = trpc.agentSelfReg.createW9Session.useMutation({
    onSuccess: (data) => {
      setW9EmbedSrc(data.embedSrc);
      setW9SubmissionId(data.submissionId);
      setStep("w9");
    },
    onError: (e: any) => toast.error(e.message || "Could not load W-9 form. Please try again."),
  });
  const completeRegistrationMutation = trpc.agentSelfReg.completeSelfRegistration.useMutation({
    onSuccess: (data) => {
      setCredentials({ username: data.username, tempPassword: data.tempPassword, dashboardUrl: data.dashboardUrl });
      setStep("welcome");
    },
    onError: (e: any) => toast.error(e.message || "Could not complete registration. Please try again."),
  });

  const handleInfoSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim()) { toast.error("First and last name are required"); return; }
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) { toast.error("Valid email is required"); return; }
    setStep("agreement");
  };

  const handleAgreementSubmit = () => {
    if (!isValidSignatureDataUrl(agreementSignature)) {
      setAgreementSignatureError("Please add your signature to sign the Agreement.");
      return;
    }
    if (!isValidPrintedName(agreementPrintedName)) {
      setAgreementNameError("Please print your full legal name to sign.");
      return;
    }
    setAgreementNameError(null);
    setAgreementSignatureError(null);
    createW9SessionMutation.mutate({ email: form.email, firstName: form.firstName, lastName: form.lastName });
  };

  const handleW9Submit = () => {
    if (!w9Signed) { toast.error("Please sign the W-9 before submitting."); return; }
    submitMutation.mutate({
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      phone: form.phone || undefined,
      referredByRepCode: referredBy,
      agentLevel: maxAllowedLevel as "Senior Associate" | "Associate",
      agreementAccepted: true,
      // Carries the signature from the agreement step. The server records it
      // against the rep it is about to create.
      agreementPrintedName: agreementPrintedName.trim(),
      agreementSignature: agreementSignature ?? "",
      agreementSignatureSource,
      w9SubmissionId: w9SubmissionId ?? undefined,
    });
  };

  const handleIdSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!idForm.legalFullName.trim()) { toast.error("Legal full name is required"); return; }
    if (!idForm.residentialAddress.trim()) { toast.error("Residential address is required"); return; }
    if (!idFile) { toast.error("Please upload a photo of your government-issued ID"); return; }
    if (!registeredRepCode || !verifyToken) { toast.error("Session error — please restart your registration."); return; }

    setIdUploading(true);
    try {
      // Authorized by the SAME verification token issued at submit time —
      // the server derives the rep from the token, never trusts a
      // client-supplied repId (see /api/upload/driver-license in
      // server/uploadRoutes.ts).
      const formData = new FormData();
      formData.append("file", idFile);
      const uploadRes = await fetch(`/api/upload/driver-license?token=${encodeURIComponent(verifyToken)}`, {
        method: "POST",
        body: formData,
      });
      if (!uploadRes.ok) throw new Error("ID upload failed");
      const { url: driverLicenseFrontUrl, fileKey: driverLicenseFrontKey } = await uploadRes.json();

      completeRegistrationMutation.mutate({
        repCode: registeredRepCode,
        token: verifyToken,
        legalFullName: idForm.legalFullName,
        residentialAddress: idForm.residentialAddress,
        driverLicenseFrontUrl,
        driverLicenseFrontKey,
      });
    } catch (err: any) {
      toast.error(err.message || "ID upload failed. Please try again.");
    } finally {
      setIdUploading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard?.writeText(text).then(
      () => toast.success("Copied to clipboard"),
      () => toast.error("Could not copy — please copy manually"),
    );
  };

  if (referrerLoading) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center p-6">
        <Loader2 className="w-8 h-8 text-[#84ab4a] animate-spin" />
      </div>
    );
  }

  if (referrerInvalid) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center p-6">
        <div className="text-center max-w-md w-full">
          <div className="w-20 h-20 rounded-full bg-red-500/10 border-2 border-red-500/30 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">Invite Link Required</h1>
          <p className="text-white/60">
            {hasRef
              ? "This invite link is invalid or has expired. Please ask your recruiter for a new link, or contact "
              : "Registration requires an invite link from your recruiter. Please use the link they sent you, or contact "}
            <a href="mailto:info@BizConnect360.com" className="text-[#84ab4a] hover:underline">info@BizConnect360.com</a>
            {" "}for help.
          </p>
        </div>
      </div>
    );
  }

  if (step === "welcome" && credentials) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center p-6">
        <div className="text-center max-w-md w-full">
          <div className="w-20 h-20 rounded-full bg-[#84ab4a]/15 border-2 border-[#84ab4a]/40 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-[#84ab4a]" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-3">Welcome to BizConnect360!</h1>
          <p className="text-white/60 mb-6">
            Your registration is complete and your dashboard is ready. Your government ID is now pending admin
            review — this doesn't block your access, but some features may be limited until it's verified.
          </p>
          <div className="bg-[#0D1E35] border border-[#84ab4a]/20 rounded-xl p-5 text-left mb-6 space-y-3">
            <div>
              <div className="text-xs text-white/40 mb-1">Username</div>
              <div className="flex items-center justify-between gap-2 bg-white/5 rounded-lg px-3 py-2">
                <span className="text-white font-mono text-sm">{credentials.username}</span>
                <button type="button" onClick={() => copyToClipboard(credentials.username)} className="text-white/40 hover:text-white">
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            {credentials.tempPassword && (
              <div>
                <div className="text-xs text-white/40 mb-1">Temporary Password</div>
                <div className="flex items-center justify-between gap-2 bg-white/5 rounded-lg px-3 py-2">
                  <span className="text-white font-mono text-sm">{credentials.tempPassword}</span>
                  <button type="button" onClick={() => copyToClipboard(credentials.tempPassword!)} className="text-white/40 hover:text-white">
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
            <p className="text-white/40 text-xs">Save these now — please change your password after your first login.</p>
          </div>
          <Button
            onClick={() => { window.location.href = credentials.dashboardUrl; }}
            className="w-full bg-[#84ab4a] hover:bg-[#6d9038] text-white font-semibold h-11"
          >
            Go to My Dashboard →
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A1628] flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <img src="https://bc360.wibiz.ai/wp-content/uploads/2026/03/bc360.png" alt="BizConnect360" className="h-12 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-1">
            Welcome to BizConnect360
          </h1>
          {referredBy ? (
            <p className="text-white/60 text-sm">
              Invited by <span className="text-[#84ab4a] font-semibold">{inviterName}</span>
            </p>
          ) : (
            <p className="text-white/60 text-sm">Start your journey with the BizConnect360 ecosystem</p>
          )}
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {["Your Info", "Agreement", "W-9", "ID Verification"].map((label, i) => {
            const stepKeys = ["info", "agreement", "w9", "id"];
            const currentIdx = stepKeys.indexOf(step);
            const isActive = currentIdx === i;
            const isDone = currentIdx > i;
            return (
              <div key={label} className="flex items-center gap-1.5">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                  isActive ? "bg-[#84ab4a] text-white" : isDone ? "bg-[#84ab4a]/30 text-[#84ab4a]" : "bg-white/10 text-white/40"
                }`}>
                  {isDone ? "✓" : i + 1}
                </div>
                <span className={`text-xs font-medium hidden sm:inline ${isActive ? "text-white" : "text-white/40"}`}>{label}</span>
                {i < 3 && <div className="w-5 sm:w-8 h-px bg-white/20" />}
              </div>
            );
          })}
        </div>

        <div className="bg-[#0D1E35] border border-white/10 rounded-2xl p-6 shadow-xl">
          {step === "info" && (
            <form onSubmit={handleInfoSubmit} className="space-y-4">
              <div className="flex items-center gap-2 mb-4 pb-4 border-b border-white/10">
                <User className="w-5 h-5 text-[#84ab4a]" />
                <span className="text-white font-semibold text-sm">Your Information</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-gray-400">First Name *</Label>
                  <Input
                    placeholder="John"
                    value={form.firstName}
                    onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-9"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-gray-400">Last Name *</Label>
                  <Input
                    placeholder="Smith"
                    value={form.lastName}
                    onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-9"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-400">Email Address *</Label>
                <Input
                  type="email"
                  placeholder="john@example.com"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-9"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-400">Phone Number</Label>
                <Input
                  type="tel"
                  placeholder="+1 (555) 000-0000"
                  value={form.phone}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-9"
                />
              </div>
              <Button type="submit" className="w-full bg-[#84ab4a] hover:bg-[#6d9038] text-white font-semibold h-10 mt-2">
                Continue to Agreement →
              </Button>
            </form>
          )}

          {step === "agreement" && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4 pb-4 border-b border-white/10">
                <Shield className="w-5 h-5 text-[#84ab4a]" />
                <span className="text-white font-semibold text-sm">Platform Participation Agreement</span>
              </div>

              <p className="text-white/60 text-xs">
                Please read the Master Agent Participation Agreement in full before signing.
              </p>

              <AgreementRevisionNotice theme="dark" />

              {/* The approved PDF itself, not a re-typeset copy, so what is read
                  is exactly what is signed. */}
              <AgreementViewer theme="dark" />

              <AgreementSignature
                theme="dark"
                printedName={agreementPrintedName}
                onPrintedNameChange={v => { setAgreementPrintedName(v); setAgreementNameError(null); }}
                signatureDataUrl={agreementSignature}
                onSignatureChange={(v, src) => { setAgreementSignature(v); setAgreementSignatureSource(src); setAgreementSignatureError(null); }}
                nameError={agreementNameError}
                signatureError={agreementSignatureError}
                disabled={createW9SessionMutation.isPending}
              />

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep("info")}
                  className="flex-1 border-white/10 text-white/60 hover:text-white h-10"
                >
                  ← Back
                </Button>
                <Button
                  type="button"
                  disabled={!canSubmitSignature(agreementPrintedName, agreementSignature) || createW9SessionMutation.isPending}
                  onClick={handleAgreementSubmit}
                  className="flex-[2] bg-[#84ab4a] hover:bg-[#6d9038] text-white font-semibold h-10 disabled:opacity-40"
                >
                  {createW9SessionMutation.isPending ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading W-9...</>
                  ) : (
                    "Sign & Continue to W-9 →"
                  )}
                </Button>
              </div>
            </div>
          )}

          {step === "w9" && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4 pb-4 border-b border-white/10">
                <FileText className="w-5 h-5 text-[#84ab4a]" />
                <span className="text-white font-semibold text-sm">W-9 Tax Form</span>
                {w9Signed && <span className="ml-auto text-xs bg-green-500/15 text-green-400 border border-green-500/20 px-2 py-0.5 rounded-full font-semibold">Signed ✓</span>}
              </div>
              <p className="text-white/60 text-xs">
                As an independent contractor, a completed W-9 is required before your application can be reviewed. Please fill in your legal name, address, and SSN/EIN, then sign below.
              </p>
              {w9EmbedSrc && !w9Signed && (
                <div className="rounded-xl overflow-hidden border border-white/10" style={{ height: 520 }}>
                  <DocusealForm
                    src={w9EmbedSrc}
                    onComplete={() => {
                      setW9Signed(true);
                      toast.success("W-9 signed! Click Submit to complete your application.");
                    }}
                    style={{ height: "100%", width: "100%" }}
                  />
                </div>
              )}
              {w9Signed && (
                <div className="flex items-center gap-3 bg-green-500/10 border border-green-500/20 rounded-xl px-4 py-3">
                  <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span className="text-sm text-green-300">W-9 signed successfully. Click Submit to complete your application.</span>
                </div>
              )}
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep("agreement")}
                  className="flex-1 border-white/10 text-white/60 hover:text-white h-10"
                  disabled={submitMutation.isPending}
                >
                  ← Back
                </Button>
                <Button
                  type="button"
                  disabled={!w9Signed || submitMutation.isPending}
                  onClick={handleW9Submit}
                  className="flex-[2] bg-[#84ab4a] hover:bg-[#6d9038] text-white font-semibold h-10 disabled:opacity-40"
                >
                  {submitMutation.isPending ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                  ) : (
                    "Continue to ID Verification →"
                  )}
                </Button>
              </div>
            </div>
          )}

          {step === "id" && (
            <form onSubmit={handleIdSubmit} className="space-y-4">
              <div className="flex items-center gap-2 mb-4 pb-4 border-b border-white/10">
                <IdCard className="w-5 h-5 text-[#84ab4a]" />
                <span className="text-white font-semibold text-sm">ID Verification</span>
              </div>
              <p className="text-white/60 text-xs">
                Last step — a government-issued ID is required to complete registration. You'll get immediate
                dashboard access once submitted; our team reviews the ID afterward.
              </p>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-400">Legal Full Name *</Label>
                <Input
                  placeholder="As it appears on your ID"
                  value={idForm.legalFullName}
                  onChange={e => setIdForm(f => ({ ...f, legalFullName: e.target.value }))}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-9"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-400">Residential Address *</Label>
                <Input
                  placeholder="123 Main St, City, State, ZIP"
                  value={idForm.residentialAddress}
                  onChange={e => setIdForm(f => ({ ...f, residentialAddress: e.target.value }))}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-9"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-400">Government-Issued ID Photo *</Label>
                <Input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  onChange={e => setIdFile(e.target.files?.[0] ?? null)}
                  className="bg-white/5 border-white/10 text-white file:text-white/80 h-9"
                />
                <p className="text-white/30 text-[11px]">JPEG, PNG, WebP, or PDF — max 10MB.</p>
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep("w9")}
                  className="flex-1 border-white/10 text-white/60 hover:text-white h-10"
                  disabled={idUploading || completeRegistrationMutation.isPending}
                >
                  ← Back
                </Button>
                <Button
                  type="submit"
                  disabled={idUploading || completeRegistrationMutation.isPending}
                  className="flex-[2] bg-[#84ab4a] hover:bg-[#6d9038] text-white font-semibold h-10 disabled:opacity-40"
                >
                  {idUploading || completeRegistrationMutation.isPending ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Completing registration...</>
                  ) : (
                    "Complete Registration →"
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>

        <p className="text-center text-white/30 text-xs mt-4">
          Questions? Contact <a href="mailto:info@BizConnect360.com" className="text-[#84ab4a] hover:underline">info@BizConnect360.com</a>
        </p>
      </div>
    </div>
  );
}
